import React, { type ChangeEvent } from 'react'

import { DraggableProvided } from 'react-beautiful-dnd'
import { HiOutlineMinusCircle } from 'react-icons/hi'
import { MdDragIndicator } from 'react-icons/md'

import { Box, Flex, IconButton, Select, Text } from '@chakra-ui/react'

import { Criterion, CriterionOption } from '../types/types'
import { AiOperatorCriterionMapper } from '../utils/utils'
import { AiOperatorCriterionInput } from './AiOperatorCriterionInput'

interface AiOperatorCriterionItemIProps {
  item: Criterion
  handleDelete: (id: string) => void
  handleUpdateCriterion: (id: string, newCriterion: CriterionOption) => void
  provided: DraggableProvided
  disabled: boolean
  availableCriteria: CriterionOption[]
}

export const AiOperatorCriterionItem = ({
  item,
  handleDelete,
  handleUpdateCriterion,
  provided,
  disabled,
  availableCriteria,
}: AiOperatorCriterionItemIProps) => {
  const selectOptions = React.useMemo(() => {
    const currentOption = item.criterion
    const otherOptions = availableCriteria.filter(
      (option) => option.value !== currentOption.value
    )
    return [currentOption, ...otherOptions]
  }, [item.criterion, availableCriteria])

  const handleCriterionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value
    const selectedCriterion = selectOptions.find(
      (option) => option.value === selectedValue
    )

    if (selectedCriterion) {
      handleUpdateCriterion(item.id, selectedCriterion)
    }
  }

  return (
    <Flex
      ref={provided.innerRef}
      {...provided.draggableProps}
      alignItems='center'
      bg={disabled ? '#F7FAFC' : '#EEF4FC'}
      borderRadius='10px'
      justifyContent='space-between'
      maxWidth='1800px'
      mb={2}
      mx='auto'
      p={{ base: '10px 10px', md: '12px 12px', '2xl': '16px 20px' }}
      width='100%'
    >
      <Flex alignItems='center' flexShrink={0} mr={2}>
        <Box
          {...provided.dragHandleProps}
          alignItems='center'
          display='flex'
          mr={2}
          opacity={disabled ? 0.5 : 1}
          pointerEvents={disabled ? 'none' : 'auto'}
        >
          <MdDragIndicator aria-label='Drag handle' />
        </Box>
        <AiOperatorCriterionInput
          aria-label='Action type'
          isDisabled={disabled}
          value={AiOperatorCriterionMapper[item.action]}
        />
      </Flex>

      <Flex
        alignItems='center'
        flex='1'
        gap={{ base: 1, md: 2, xl: 3, '2xl': 4 }}
        justifyContent='center'
        maxWidth='1200px'
        minWidth='0'
      >
        <Flex
          alignItems='center'
          flexShrink={0}
          justifyContent='center'
          minWidth={{ base: '20px', '2xl': '30px' }}
        >
          <Text fontSize={{ base: 'sm', '2xl': 'md' }} textAlign='center'>
            if
          </Text>
        </Flex>

        <Select
          _focus={{ border: 'none', boxShadow: 'none' }}
          _hover={{ border: 'none' }}
          aria-label='Criterion type'
          bg='white'
          border='none'
          borderRadius='lg'
          flex='1'
          isDisabled={disabled}
          isReadOnly
          maxWidth={{
            base: '180px',
            sm: '230px',
            md: '400px',
            lg: '500px',
            xl: '700px',
            '2xl': '900px',
          }}
          minWidth={{
            base: '60px',
            sm: '70px',
            md: '120px',
            lg: '150px',
            xl: '200px',
            '2xl': '250px',
          }}
          onChange={handleCriterionChange}
          size='sm'
          value={item.criterion.value}
        >
          {selectOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <Flex
          alignItems='center'
          flexShrink={0}
          justifyContent='center'
          minWidth={{ base: '20px', '2xl': '30px' }}
        >
          <Text fontSize={{ base: 'sm', '2xl': 'md' }} textAlign='center'>
            is
          </Text>
        </Flex>
      </Flex>

      <Flex alignItems='center' flexShrink={0} ml={2}>
        <AiOperatorCriterionInput
          aria-label='Detection state'
          isDisabled={disabled}
          value={item.detected ? 'Detected' : 'Not Detected'}
        />
        {!disabled && (
          <IconButton
            _focus={{ border: 'none', boxShadow: 'none' }}
            aria-label='Delete criterion'
            colorScheme='gray'
            icon={<HiOutlineMinusCircle />}
            ml={{ base: 1, '2xl': 2 }}
            onClick={() => handleDelete(item.id)}
            size='sm'
            variant='ghost'
          />
        )}
      </Flex>
    </Flex>
  )
}
